import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const saved = localStorage.getItem('edupulse_theme') as Theme | null;
      if (saved === 'dark' || saved === 'light') return saved;
    } catch {}
    // Fall back to Telegram color scheme or light
    try {
      const tgScheme = window.Telegram?.WebApp?.colorScheme;
      if (tgScheme === 'dark' || tgScheme === 'light') return tgScheme;
    } catch {}
    return 'light';
  });

  // Apply theme — sets CSS variables as inline styles (overrides Telegram's theme)
  // Wrapped in try/catch to prevent black screen on mobile
  useEffect(() => {
    try {
      const root = document.documentElement;

      if (theme === 'dark') {
        root.style.setProperty('--tg-theme-bg-color', '#1c1c1e');
        root.style.setProperty('--tg-theme-text-color', '#ffffff');
        root.style.setProperty('--tg-theme-hint-color', '#98989e');
        root.style.setProperty('--tg-theme-link-color', '#64b5f6');
        root.style.setProperty('--tg-theme-button-color', '#64b5f6');
        root.style.setProperty('--tg-theme-button-text-color', '#000000');
        root.style.setProperty('--tg-theme-secondary-bg-color', '#2c2c2e');
        root.style.setProperty('--tg-theme-section-bg-color', '#1c1c1e');
        root.style.setProperty('--tg-theme-section-separator-color', '#38383a');
        root.style.setProperty('--tg-theme-destructive-text-color', '#ff6b6b');
      } else {
        root.style.setProperty('--tg-theme-bg-color', '#ffffff');
        root.style.setProperty('--tg-theme-text-color', '#000000');
        root.style.setProperty('--tg-theme-hint-color', '#999999');
        root.style.setProperty('--tg-theme-link-color', '#2481cc');
        root.style.setProperty('--tg-theme-button-color', '#2481cc');
        root.style.setProperty('--tg-theme-button-text-color', '#ffffff');
        root.style.setProperty('--tg-theme-secondary-bg-color', '#f4f4f5');
        root.style.setProperty('--tg-theme-section-bg-color', '#ffffff');
        root.style.setProperty('--tg-theme-section-separator-color', '#e5e5ea');
        root.style.setProperty('--tg-theme-destructive-text-color', '#ff3b30');
      }
    } catch (e) {
      console.warn('[Theme] Failed to apply theme:', e);
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setThemeState(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('edupulse_theme', next);
      return next;
    });
  }, []);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    localStorage.setItem('edupulse_theme', t);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
